import type { EntityType, SearchTypeName } from '@/lib/search';

export const PERSON_KIND = 'person';
export const CROSS_TYPE_MERGE_EXCLUDED = new Set<string>(['ahoy_user']);

/**
 * The three types that predate the generic same-type merge rule below. They
 * keep their pre-existing behavior unconditionally — including the person-kind
 * expansion for contacts — so the merge action never flickers/vanishes for
 * them while `useObjectTypes` is still loading the kinds map.
 */
const LEGACY_MERGE_HOST_TYPES: ReadonlySet<string> = new Set([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
]);
const PERSON_SEARCH_TYPES: readonly EntityType[] = ['ahoy_contact'];

/**
 * Entity kinds that are structural rather than mergeable records — merging
 * two stages or two pipelines (or a reporting rollup) doesn't make sense as a
 * user action even though the backend's merge validator would currently
 * permit it for same-type pairs.
 */
const STRUCTURAL_MERGE_EXCLUDED_KINDS: ReadonlySet<string> = new Set([
  'pipeline',
  'stage',
  'reporting',
]);

export function canMergeEntityTypes(
  aType: string,
  bType: string,
  entityKindsMap: ReadonlyMap<string, readonly string[]>
): boolean {
  if (aType === bType) {
    return true;
  }
  const aKinds = entityKindsMap.get(aType) ?? [];
  const bKinds = entityKindsMap.get(bType) ?? [];
  return (
    aKinds.includes(PERSON_KIND) &&
    bKinds.includes(PERSON_KIND) &&
    !CROSS_TYPE_MERGE_EXCLUDED.has(aType) &&
    !CROSS_TYPE_MERGE_EXCLUDED.has(bType)
  );
}

export function getMergeCandidateSearchTypes(
  entityTypeName: string,
  entityKindsMap: ReadonlyMap<string, readonly string[]>
): SearchTypeName[] {
  // Fast path: the three legacy host types keep today's behavior WITHOUT
  // consulting the kinds map (see LEGACY_MERGE_HOST_TYPES).
  if (LEGACY_MERGE_HOST_TYPES.has(entityTypeName)) {
    const result = new Set<SearchTypeName>([entityTypeName]);
    if (
      (entityKindsMap.get(entityTypeName) ?? []).includes(PERSON_KIND) &&
      !CROSS_TYPE_MERGE_EXCLUDED.has(entityTypeName)
    ) {
      for (const type of PERSON_SEARCH_TYPES) {
        if (!CROSS_TYPE_MERGE_EXCLUDED.has(type)) {
          result.add(type);
        }
      }
    }
    return Array.from(result);
  }

  // Every other type: same-type candidates only, so "Merge records" becomes
  // available on custom types and system types like ahoy_task/ahoy_meeting.
  //
  // NOTE: the backend merge validator has historically allowed ALL same-type
  // merges (no kind restriction). This UI rule is DELIBERATELY STRICTER than
  // the backend — it also excludes ahoy_user hosts and types the schema
  // hasn't loaded yet (or doesn't recognize). The backend-side structural-kind
  // check is a separate phase in this plan, not a pre-existing rule this is
  // mirroring.
  //
  // `*_timeline` names are reserved projection aliases of an underlying entity
  // type — the backend `/search` endpoint rejects them (see
  // `isValidEntityTypeName` in `lib/crm/core/types.ts` and the same filter in
  // `use-grant-scopings.ts`). Reject them here so a schema that surfaces one can
  // never make `EntityTypeahead` issue an invalid `/search` request.
  if (
    CROSS_TYPE_MERGE_EXCLUDED.has(entityTypeName) ||
    entityTypeName.endsWith('_timeline')
  ) {
    return [];
  }
  const kinds = entityKindsMap.get(entityTypeName);
  if (!kinds) {
    // Schema not loaded yet, or an unknown type — fail closed.
    return [];
  }
  if (kinds.some(kind => STRUCTURAL_MERGE_EXCLUDED_KINDS.has(kind))) {
    return [];
  }
  return [entityTypeName];
}
